import { logAndAddBreadcrumb, logAndCaptureMessage } from './errorTools'

// A stale-chunk hard reload writes this timestamp so a second failure in quick succession does not
// loop (i.e. a reload that didn't fix things — offline, or the new deploy is also missing the chunk).
const RELOAD_GUARD_KEY = 'dfh:staleChunkReloadAt'
const RELOAD_COOLDOWN_MS = 10_000

// Sentry groups messages by their text, so these stay constant and the failing chunk travels in the
// event's extra data instead. Interpolating the hashed chunk URL fanned a single problem out into a
// fresh issue per deploy.
const RELOAD_MESSAGE = 'Reloading to recover from stale chunk'
const SUPPRESSED_MESSAGE = 'Stale-chunk reload suppressed by loop guard'
const TRANSIENT_MESSAGE = 'Skipping reload for transient chunk load failure'

// Chunk-load failures surface with different messages across browsers and loaders (webpack's
// `ChunkLoadError`, native dynamic-import failures), so match the known shapes rather than one string.
const CHUNK_ERROR_PATTERNS: readonly RegExp[] = [
  /ChunkLoadError/i,
  /Loading (?:CSS )?chunk .+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
]

const hasMessage = (value: unknown): value is { message: unknown } =>
  typeof value === 'object' && value !== null && 'message' in value

const messageOf = (error: unknown): string => {
  if (typeof error === 'string') return error
  if (error instanceof Error) return `${error.name}: ${error.message}`
  if (hasMessage(error) && typeof error.message === 'string') return error.message
  return ''
}

export const isChunkLoadError = (error: unknown): boolean => {
  const message = messageOf(error)
  return message !== '' && CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message))
}

// webpack tags the chunk-load-timeout path as `(timeout: <url>)` (vs `(error: …)` / `(missing: …)`
// for a removed/404 chunk). A timeout is a slow/dropped connection, not a stale deploy — reloading
// into the same bad network just fails again — so we detect it and skip the reload.
const TIMEOUT_MARKER = /\(timeout:/i

const isConnectionTimeout = (message: string): boolean => TIMEOUT_MARKER.test(message)

const isOffline = (): boolean => typeof navigator !== 'undefined' && navigator.onLine === false

const readLastReloadAt = (): number => {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY)) || 0
  } catch {
    // sessionStorage access can throw when storage is disabled; treat as "never reloaded".
    return 0
  }
}

const writeLastReloadAt = (value: number): void => {
  try {
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(value))
  } catch {
    // ignore — best-effort loop guard
  }
}

// Callers that swallow the failed subtree (ChunkErrorBoundary) need to know whether a reload is
// actually coming. Both skip answers mean nothing is going to repair the page on its own, so the
// caller must render a fallback instead of nothing; they differ in what could still change that.
export type StaleChunkReloadDecision =
  | 'reload-scheduled'
  // Skipped over a transient network condition — offline, or webpack's `(timeout:)` variant. Includes
  // a loop-guard skip where the connection dropped after the reload, so a reconnect can plausibly
  // recover and callers may wait for one.
  | 'reload-skipped-transient'
  // Skipped and no change in connectivity will help: the loop guard fired while the client is online,
  // meaning the reload itself didn't recover the chunk.
  | 'reload-skipped'
  // No window to reload (SSR/build) — not a decision about the chunk at all.
  | 'no-window'

/**
 * Recover from a stale-chunk failure (an old client requesting a hashed chunk removed by a redeploy)
 * by hard-reloading once to pick up the current chunk map. Guarded so repeated failures don't loop.
 */
export const reloadOnceForStaleChunk = (error: unknown): StaleChunkReloadDecision => {
  // No window to reload (SSR/build). Kept distinct from the skip decisions: callers only act on this
  // during a client render, so it is never seen, but reporting it as a skip would invite a caller to
  // render "check your connection" into static HTML.
  if (typeof window === 'undefined') return 'no-window'

  const message = messageOf(error)
  // A reload can only recover a chunk the client is able to fetch: not a connection timeout, and not
  // while offline. Both are transient network conditions a forced reload cannot fix — it would just
  // interrupt the user and fail again.
  const transient = isConnectionTimeout(message) || isOffline()

  const now = Date.now()
  // Checked ahead of the transient skip: a failure this soon after a reload means the reload did not
  // recover the chunk, which is the one genuinely actionable signal — including (especially) when the
  // connection has dropped since, which is how a reload makes things worse.
  if (now - readLastReloadAt() < RELOAD_COOLDOWN_MS) {
    logAndCaptureMessage(SUPPRESSED_MESSAGE, {
      level: 'warning',
      fingerprint: [SUPPRESSED_MESSAGE],
      extra: { chunkError: message, transient },
    })
    // A reconnect is only worth waiting for if the connection is what broke.
    return transient ? 'reload-skipped-transient' : 'reload-skipped'
  }

  if (transient) {
    // Deliberately not a Sentry event: high volume, caused by the user's network, nothing to action.
    // The breadcrumb still attaches it to any real error captured later in the session.
    logAndAddBreadcrumb({
      category: 'chunk-load',
      level: 'info',
      message: TRANSIENT_MESSAGE,
      data: { chunkError: message },
    })
    return 'reload-skipped-transient'
  }

  writeLastReloadAt(now)
  // Expected self-healing recovery — 'info' keeps it out of the error/warning stream.
  logAndCaptureMessage(RELOAD_MESSAGE, {
    level: 'info',
    fingerprint: [RELOAD_MESSAGE],
    extra: { chunkError: message },
  })
  window.location.reload()
  return 'reload-scheduled'
}
